import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  LinearProgress,
  Chip,
} from '@mui/material'
import {
  People as PeopleIcon,
  Upload as UploadIcon,
  Assessment as AssessmentIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material'
import { studentsApi, analysisApi, reportsApi, settingsApi } from '../lib/api'
import { AnalysisTask, Quota } from '../types'

export default function Dashboard() {
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    studentCount: 0,
    taskCount: 0,
    completedCount: 0,
    reportCount: 0,
  })
  const [quota, setQuota] = useState<Quota | null>(null)
  const [recentTasks, setRecentTasks] = useState<AnalysisTask[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboard()
  }, [])

  const loadDashboard = async () => {
    try {
      const [students, tasks, reports, quotaData] = await Promise.all([
        studentsApi.getAll(),
        analysisApi.getTasks(),
        reportsApi.getAll(),
        settingsApi.getQuota(),
      ])

      setStats({
        studentCount: students.length,
        taskCount: tasks.length,
        completedCount: tasks.filter(t => t.status === 'completed').length,
        reportCount: reports.length,
      })

      setQuota(quotaData)
      setRecentTasks(tasks.slice(0, 5))
    } catch (err) {
      console.error('Failed to load dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  const statCards = [
    {
      title: '学生总数',
      value: stats.studentCount,
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: 'primary.main',
    },
    {
      title: '分析任务',
      value: `${stats.completedCount}/${stats.taskCount}`,
      icon: <UploadIcon sx={{ fontSize: 40, color: 'secondary.main' }} />,
      color: 'secondary.main',
    },
    {
      title: '学情报告',
      value: stats.reportCount,
      icon: <AssessmentIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: 'success.main',
    },
    {
      title: '今日额度',
      value: quota ? `${quota.dailyImagesUsed}/${quota.dailyImagesLimit}` : '-',
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: 'warning.main',
    },
  ]

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <Typography>加载中...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        工作台
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map(card => (
          <Grid item xs={12} sm={6} md={3} key={card.title}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="body2">
                      {card.title}
                    </Typography>
                    <Typography variant="h4">{card.value}</Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">最近任务</Typography>
                <Button size="small" onClick={() => navigate('/analysis')}>
                  查看全部
                </Button>
              </Box>
              {recentTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  暂无分析任务
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {recentTasks.map(task => (
                    <Box
                      key={task.id}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          任务ID: {task.id.slice(0, 8)}...
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          学生: {task.studentId}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={task.status}
                          size="small"
                          color={
                            task.status === 'completed'
                              ? 'success'
                              : task.status === 'failed'
                              ? 'error'
                              : task.status === 'processing'
                              ? 'info'
                              : 'default'
                          }
                        />
                        {task.status === 'processing' && (
                          <Box sx={{ width: 100 }}>
                            <LinearProgress variant="determinate" value={task.progress} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                快速操作
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={() => navigate('/analysis')}
                  fullWidth
                >
                  上传作业分析
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate('/students')}
                  fullWidth
                >
                  管理学生
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => navigate('/reports')}
                  fullWidth
                >
                  查看报告
                </Button>
              </Box>
            </CardContent>
          </Card>

          {quota && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  额度使用
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">今日图片</Typography>
                      <Typography variant="body2">
                        {quota.dailyImagesUsed}/{quota.dailyImagesLimit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(quota.dailyImagesUsed / quota.dailyImagesLimit) * 100}
                      color={quota.dailyImagesUsed >= quota.dailyImagesLimit ? 'error' : 'primary'}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">本月练习</Typography>
                      <Typography variant="body2">
                        {quota.monthlyExercisesUsed}/{quota.monthlyExercisesLimit}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(quota.monthlyExercisesUsed / quota.monthlyExercisesLimit) * 100}
                      color={quota.monthlyExercisesUsed >= quota.monthlyExercisesLimit ? 'error' : 'secondary'}
                    />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">存储空间</Typography>
                      <Typography variant="body2">
                        {quota.storageUsed}MB / {quota.storageLimit}MB
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={quota.storageLimit > 0 ? (quota.storageUsed / quota.storageLimit) * 100 : 0}
                      color={quota.storageUsed >= quota.storageLimit ? 'error' : 'warning'}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}
